/**
 * Centralized SEO Configuration
 * 
 * IMPORTANT: Route paths MUST match exactly with routes in App.js
 * Last synced: 2024-12-05
 */

export const defaultSEO = {
  title: "Raven Environmental | Alaska Compliance & Consulting",
  description: "Expert environmental consulting in Alaska. Specializing in EPA compliance, air quality permitting, dispersion modeling, and NEPA/ESA assessments."
};

export const seoRoutes = {
  // ==========================================
  // CORE PAGES
  // ==========================================
  "/": {
    title: "Raven Environmental | Alaska Environmental Consulting & Compliance",
    description: "Premier environmental consulting for Alaska. We handle EPA audits, air quality permitting, and regulatory compliance for mining, oil & gas, and industrial sectors."
  },
  "/home": {
    title: "Raven Environmental | Alaska Environmental Consulting & Compliance",
    description: "Premier environmental consulting for Alaska. We handle EPA audits, air quality permitting, and regulatory compliance for mining, oil & gas, and industrial sectors."
  },
  "/about": {
    title: "About Raven Environmental | Independent Alaska Consultants",
    description: "Independently owned environmental consultancy serving Fairbanks, Anchorage, and remote Alaska. We bridge the gap between industry goals and regulatory requirements."
  },
  "/services": {
    title: "Environmental Services | Permitting, Audits & Modeling",
    description: "Comprehensive environmental services: Air quality permits (Title V), dispersion modeling (AERMOD), NEPA/ESA consulting, and compliance auditing."
  },
  "/industries": {
    title: "Industries Served | Mining, Oil & Gas, Power Generation",
    description: "Specialized environmental support for Alaska's key sectors: Mining operations, Oil & Gas extraction, Power Utilities, and Military infrastructure."
  },
  "/contact": {
    title: "Contact Raven Environmental | Schedule a Consultation",
    description: "Get a quote for environmental consulting services. Based in Alaska, ready to deploy statewide. Contact us today."
  },

  // ==========================================
  // SERVICE PAGES
  // ==========================================
  "/services/compliance-support": {
    title: "Compliance Reporting & Support | Ongoing Monitoring",
    description: "Ongoing support for environmental compliance reports, discharge monitoring, and record-keeping."
  },
  "/services/regulatory-guidance": {
    title: "Regulatory Guidance Services | EPA & ADEC Compliance",
    description: "Expert regulatory guidance for environmental compliance. Navigate EPA and ADEC requirements with confidence."
  },
  "/services/permit-applications": {
    title: "Air Permit Applications | Title V & Minor Source",
    description: "Professional preparation of air quality permit applications for Alaska ADEC and EPA compliance."
  },
  "/services/cost-effective-solutions": {
    title: "Strategic Environmental Planning | Cost-Effective Compliance",
    description: "Capital-efficient environmental project planning. We optimize compliance strategies to reduce operational costs."
  },

  // ==========================================
  // SECTOR/INDUSTRY PAGES
  // ==========================================
  "/sectors/oil-and-gas": {
    title: "Oil & Gas Environmental Consulting | Upstream & Midstream",
    description: "Regulatory support for Alaska's Oil & Gas sector. Pipeline permitting, emissions reporting, and drilling compliance management."
  },
  "/sectors/mining": {
    title: "Mining Environmental Services | Exploration to Reclamation",
    description: "Support for hard rock and placer mining. Excavation permitting, water quality compliance, and environmental stewardship for Alaska mines."
  },
  "/sectors/power-utilities": {
    title: "Power & Utility Permitting | Emissions & Regulatory Support",
    description: "Environmental compliance for power plants and utilities. Stack testing coordination, emissions inventory, and operational permitting."
  },
  "/sectors/power-plants": {
    title: "Power Plant Environmental Consulting | Alaska Utilities",
    description: "Environmental compliance for power plants and utilities. Stack testing coordination, emissions inventory, and operational permitting."
  },
  "/sectors/coal-fired-power-plants": {
    title: "Coal-Fired Power Plant Compliance | Alaska Environmental",
    description: "Specialized environmental support for coal-fired power plants. Air quality permitting, emissions monitoring, and regulatory compliance."
  },
  "/sectors/construction": {
    title: "Construction Environmental Compliance | SWPPP & Permitting",
    description: "Keeping construction projects on track. Stormwater management, site assessments, and regulatory clearance for development projects."
  },
  "/sectors/military-installations": {
    title: "Military Installation Environmental Support | Federal Compliance",
    description: "Specialized environmental support for military installations. NEPA compliance, hazardous waste management, and federal regulatory expertise."
  },
  "/sectors/seafood-processing": {
    title: "Seafood Processing Environmental Compliance | Alaska DEC",
    description: "Environmental compliance for Alaska's seafood processing industry. Wastewater permitting, air quality, and odor control."
  },
  "/sectors/soil-remediation": {
    title: "Soil Remediation Consulting | Contaminated Site Management",
    description: "Expert soil remediation services for contaminated sites. Site assessment, cleanup planning, and regulatory compliance."
  },
  "/sectors/geothermal-permitting": {
    title: "Geothermal Energy Permitting | Alaska Environmental",
    description: "Environmental permitting for geothermal energy projects. Air quality, water quality, and NEPA compliance."
  },
  "/sectors/antimony-roasting": {
    title: "Antimony Roasting Environmental Compliance | Air Quality",
    description: "Specialized environmental support for antimony roasting operations. Air quality permitting and emissions control."
  },
  "/sectors/mining-construction": {
    title: "Mining Construction Permitting | Alaska Environmental",
    description: "Permitting and compliance for mine site construction and development. Environmental planning for Alaska mining projects."
  },

  // ==========================================
  // CONTACT LANDING PAGES
  // ==========================================
  "/contact/oil-and-gas": {
    title: "Request Oil & Gas Compliance Quote | Raven Environmental",
    description: "Get a quote for oil and gas environmental permitting and compliance."
  },
  "/contact/power-plants": {
    title: "Power Plant Compliance Inquiry | Raven Environmental",
    description: "Contact us regarding power plant environmental support and permitting."
  },
  "/contact/coal-fired": {
    title: "Coal-Fired Power Plant Inquiry | Raven Environmental",
    description: "Contact us regarding coal-fired power plant environmental compliance."
  },
  "/contact/military": {
    title: "Federal/Military Project Inquiry | Raven Environmental",
    description: "Contact us regarding federal and military environmental contracting."
  },
  "/contact/seafood-processing": {
    title: "Seafood Processing Compliance | Alaska DEC Support",
    description: "Environmental compliance support for Alaska seafood processors and canneries."
  },
  "/contact/soil-remediation": {
    title: "Soil Remediation Consulting | Contaminated Site Support",
    description: "Consulting for soil remediation and contaminated site management."
  },
  "/contact/geothermal": {
    title: "Geothermal Permitting & Compliance | Raven Environmental",
    description: "Environmental support for geothermal energy exploration and production."
  },
  "/contact/mining-construction": {
    title: "Mining Construction Permitting | Raven Environmental",
    description: "Permitting and compliance for mine site construction and development."
  },
  "/contact/antimony-roasting": {
    title: "Antimony Roasting Compliance | Raven Environmental",
    description: "Environmental compliance support for antimony roasting operations."
  },

  // ==========================================
  // OTHER UTILITY PAGES
  // ==========================================
  "/permit-feasibility": {
    title: "Permit Feasibility Strategy | Raven Environmental",
    description: "Strategic planning for environmental permitting. Assess feasibility and timelines before you apply."
  },
  "/permit-applications": {
    title: "Environmental Permit Applications | Alaska Permitting",
    description: "Expert assistance with environmental permit applications. Title V, Minor Source, PSD, and specialty permits for Alaska facilities."
  },
  "/equipment-selection": {
    title: "Environmental Equipment Selection | Monitoring & Controls",
    description: "Guidance on selecting compliant emissions control devices and monitoring equipment."
  },
  "/pollution-control": {
    title: "BACT & Emissions Controls | Air Quality Compliance",
    description: "Best Available Control Technology (BACT) analysis and pollution control strategy."
  },
  "/compliance-monitoring": {
    title: "Compliance Monitoring Services | EPA & ADEC",
    description: "Ongoing compliance monitoring services. Regular reporting, emissions tracking, and regulatory oversight management."
  },
  "/regulatory-support": {
    title: "Regulatory Guidance | ADEC & EPA Interpretations",
    description: "Expert interpretation of complex environmental regulations and compliance gray areas."
  },
  "/modeling-technical-support": {
    title: "Dispersion Modeling Services | AERMOD & CALPUFF Consultants",
    description: "Expert air dispersion modeling using AERMOD and CALPUFF. Gaussian plume models, PSD permitting, and BACT analysis."
  },
  "/cost-effective-inquiry": {
    title: "Cost-Effective Solutions Inquiry | Raven Environmental",
    description: "Request information about our cost-effective environmental consulting solutions for Alaska businesses."
  },

  // ==========================================
  // LEGAL PAGES
  // ==========================================
  "/privacy-policy": {
    title: "Privacy Policy | Raven Environmental",
    description: "Privacy policy for Raven Environmental Consulting, LLC. Learn how we protect your personal information."
  },
  "/terms-of-service": {
    title: "Terms of Service | Raven Environmental",
    description: "Terms of service for Raven Environmental Consulting, LLC website and services."
  },

  // ==========================================
  // ADMIN PAGES (Basic SEO)
  // ==========================================
  "/admin": {
    title: "Admin Dashboard | Raven Environmental",
    description: "Administrative dashboard for Raven Environmental Consulting."
  },
  "/admin/login": {
    title: "Admin Login | Raven Environmental",
    description: "Secure login for Raven Environmental administrators."
  }
};
